import React, { useMemo, useState } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useAuth } from '../../contexts/AuthContext';
import { getShop, describeShopItem } from '../../lib/shops';
import {
  totalInGp, totalInCp, formatPriceCp, currentWeight, carryCapacity,
  encumbranceTier, ENCUMBRANCE_LABELS,
} from '../../lib/inventory';

const CATEGORY_ICONS = { weapon: '⚔', armor: '🛡', consumable: '🧪', gear: '🎒', treasure: '💎' };

export default function ShopPanel({ shopId, shopkeeperName, onClose }) {
  const { players, purchaseFromShop } = useCampaign();
  const { auth } = useAuth();
  const playerId = auth?.playerId;
  const player   = playerId ? players?.[playerId] : null;

  const shop = useMemo(() => getShop(shopId), [shopId]);
  const [submitting, setSubmitting] = useState(null);    // itemName being bought
  const [error, setError]           = useState(null);    // {itemName, reason}
  const [toast, setToast]           = useState(null);    // {itemName, ts}

  if (!shop || !player) return null;

  const items = (shop.items || [])
    .map((i) => describeShopItem(shopId, i.name))
    .filter(Boolean);

  const str       = player.abilityScores?.str ?? 10;
  const size      = player.sizeCategory || 'medium';
  const cap       = carryCapacity(str, size);
  const wt        = currentWeight(player.inventory);
  const tier      = encumbranceTier(wt, str, size);
  const tierMeta  = ENCUMBRANCE_LABELS[tier];
  const wallet    = player.currency || {};
  const walletCp  = totalInCp(wallet);
  const headline  = `${shopkeeperName || shop.defaultShopkeeperName || shop.name} · ${shop.name}`;

  const handleBuy = async (item) => {
    if (submitting) return;
    setSubmitting(item.name);
    setError(null);
    try {
      const res = await purchaseFromShop(playerId, shopId, item.name);
      if (!res?.ok) {
        setError({ itemName: item.name, reason: res?.reason || 'Purchase failed' });
      } else {
        setToast({ itemName: item.name, ts: Date.now() });
        setTimeout(() => setToast((t) => t?.itemName === item.name ? null : t), 1800);
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <div style={styles.title}>
            <span style={styles.shopIcon}>{shop.icon}</span>
            <span>{headline}</span>
          </div>
          <button style={styles.closeBtn} onClick={onClose} title="Close">✕</button>
        </div>
        <div style={styles.subheader}>
          <div style={styles.walletLine}>
            <span style={styles.walletLabel}>Purse:</span>
            <span style={styles.walletValue}>{totalInGp(wallet)} gp</span>
            <span style={styles.walletDetail}>
              ({wallet.pp || 0}pp · {wallet.gp || 0}gp · {wallet.ep || 0}ep · {wallet.sp || 0}sp · {wallet.cp || 0}cp)
            </span>
          </div>
          <div style={styles.carryLine}>
            <span style={styles.walletLabel}>🏋 Carry:</span>
            <span style={{ ...styles.walletValue, color: tierMeta.color }}>{wt.toFixed(1)} / {cap} lb</span>
            <span style={{ ...styles.walletDetail, color: tierMeta.color }}>{tierMeta.short}</span>
          </div>
        </div>

        <div style={styles.list}>
          {items.map((item) => {
            const affordable = walletCp >= item.priceCp;
            const tooHeavy   = wt + (item.weightLb || 0) > cap;
            const isBusy     = submitting === item.name;
            const buyDisabled = !affordable || tooHeavy || isBusy;
            const errFor     = error?.itemName === item.name ? error.reason : null;
            const justBought = toast?.itemName === item.name;
            const reason     = errFor || (tooHeavy ? 'Too heavy' : (!affordable ? `Need ${formatPriceCp(item.priceCp - walletCp)} more` : null));
            return (
              <div key={item.name} style={{ ...styles.row, ...(justBought ? styles.rowFlash : {}) }}>
                <span style={styles.catIcon}>{CATEGORY_ICONS[item.category] || '·'}</span>
                <div style={styles.rowMain}>
                  <div style={styles.rowName}>{item.name}</div>
                  <div style={styles.rowDesc}>{item.description}</div>
                </div>
                <div style={styles.rowMeta}>
                  <div style={styles.rowWeight}>{(item.weightLb || 0)} lb</div>
                  <div style={styles.rowPrice}>{formatPriceCp(item.priceCp)}</div>
                </div>
                <button
                  style={{ ...styles.buyBtn, ...(buyDisabled ? styles.buyBtnDisabled : {}) }}
                  onClick={() => handleBuy(item)}
                  disabled={buyDisabled}
                  title={reason || `Buy ${item.name}`}
                >
                  {isBusy ? '…' : justBought ? '✓' : 'Buy'}
                </button>
              </div>
            );
          })}
        </div>

        {error && (
          <div style={styles.errBar}>
            {error.itemName}: {error.reason}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 80,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 12,
  },
  panel: {
    width: '100%', maxWidth: 560, maxHeight: '90vh',
    background: 'linear-gradient(180deg, #1a2a48, #142038)',
    border: '2px solid #8b7340',
    borderRadius: 8,
    boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
    color: '#f0e6d0',
    fontFamily: "'Press Start 2P', monospace",
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid #4a3d28',
    background: 'rgba(0,0,0,0.25)',
  },
  title:    { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  shopIcon: { fontSize: 18 },
  closeBtn: { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 18, cursor: 'pointer', padding: '0 4px' },
  subheader:{ padding: '8px 14px', borderBottom: '1px solid #4a3d28', display: 'flex', flexDirection: 'column', gap: 4, background: 'rgba(0,0,0,0.15)' },
  walletLine:{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12 },
  carryLine: { display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12 },
  walletLabel: { color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.04em' },
  walletValue: { color: '#d4a847', fontWeight: 800, fontFamily: 'system-ui, sans-serif', fontVariantNumeric: 'tabular-nums' },
  walletDetail:{ color: '#6a5d45', fontSize: 10, fontFamily: 'system-ui, sans-serif' },
  list: { flex: 1, overflowY: 'auto', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 },
  row: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 10px',
    background: 'rgba(20,32,56,0.6)',
    border: '1px solid #2a3552',
    borderRadius: 4,
    transition: 'background 0.25s',
  },
  rowFlash: { background: 'rgba(74,222,128,0.18)', borderColor: '#4ade80' },
  catIcon: { fontSize: 16, minWidth: 18, textAlign: 'center', color: '#9a8b6e' },
  rowMain: { flex: 1, minWidth: 0 },
  rowName: { fontSize: 13, fontWeight: 700, color: '#f0e6d0' },
  rowDesc: { fontSize: 11, color: '#9a8b6e', fontFamily: 'system-ui, sans-serif', lineHeight: 1.3, marginTop: 2 },
  rowMeta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: 70 },
  rowWeight: { fontSize: 10, color: '#6a5d45', fontVariantNumeric: 'tabular-nums', fontFamily: 'system-ui, sans-serif' },
  rowPrice:  { fontSize: 12, fontWeight: 800, color: '#d4a847', fontVariantNumeric: 'tabular-nums' },
  buyBtn: {
    padding: '6px 12px',
    background: 'linear-gradient(180deg, #d4a847, #a68330)',
    border: '1px solid #c4a44a',
    color: '#1a0e00',
    fontSize: 12, fontWeight: 800, fontFamily: "'Press Start 2P', monospace",
    cursor: 'pointer', borderRadius: 4,
    minWidth: 50,
    letterSpacing: '0.04em',
  },
  buyBtnDisabled: {
    background: '#1a2a48', color: '#4a3d28', border: '1px solid #2a3552',
    cursor: 'not-allowed', boxShadow: 'none',
  },
  errBar: {
    padding: '8px 14px', background: 'rgba(192,57,43,0.12)', borderTop: '1px solid #c0392b',
    fontSize: 12, color: '#f87171',
  },
};
